import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../hooks/useAuth';
import { fetchUser } from '../../features/auth/authSlice';
import { useUpdateMutation, useGetAdminCountQuery } from '../../app/services/users';
import { getLanguages, getPermissions } from '../../constants/options';
import { SettingsCard, SettingsCardHeader, SettingsCardContent } from '../../components/SettingsCard';
import {
  Box,
  FormControl,
  FormHelperText,
  MenuItem,
  TextField,
  Typography,
} from '@mui/material';

import { validateFormData, getValidationErrorMessage } from '../../helpers';
import { putUserSchema } from '../../schemas';
import { commonTextFieldStylesWithDisabled } from '../../utils/textFieldStyles';
import { useUnsavedChangesWarning } from '../../hooks/useUnsavedChangesWarning';
import ConfirmDiscardDialog from '../../components/ConfirmDiscardDialog';

function AccountTab({ onEmailChange }) {
  const { t } = useTranslation();
  const permissions = getPermissions(t);
  const languages = getLanguages(t);
  const dispatch = useDispatch();
  const { user } = useAuth();
  const [updateUser] = useUpdateMutation();


  const initialForm = {
    firstname: '',
    lastname: '',
    email: '',
    password: '',
    language: '',
    role: '',
    status: '',
  };
  const [form, setForm] = useState(initialForm);

  useEffect(() => {
    if (user) {
      setForm(user);
    }
  }, [user]);

  // Check if form has changes
  const isDirty = JSON.stringify(form) !== JSON.stringify(user);
  const { confirmProps } = useUnsavedChangesWarning(isDirty);

  const { data: adminCountData } = useGetAdminCountQuery(undefined, {
    skip: user?.role !== 'admin',
  });
  const isLastAdmin =
    user?.role === 'admin' &&
    user?.status === 'active' &&
    adminCountData?.activeAdmins === 1;

  const handleChange = (e) => {
    const { name, value } = e.target;

    switch (name) {
      case 'role':
        setForm({
          ...form,
          role: value,
        });
        break;
      case 'language':
        setForm({
          ...form,
          language: value,
        });
        break;
      default:
        setForm({
          ...form,
          [name]: value,
        });
    }
  };

  const [formValidationErrors, setFormValidationErrors] = useState(null);
  const [serverError, setServerError] = useState('');
  const firstNameError = getValidationErrorMessage(formValidationErrors, 'firstname');
  const lastNameError = getValidationErrorMessage(formValidationErrors, 'lastname');
  const emailError = getValidationErrorMessage(formValidationErrors, 'email');
  const languageError = getValidationErrorMessage(formValidationErrors, 'language');
  const roleError = getValidationErrorMessage(formValidationErrors, 'role');

  const handleUpdateUser = async (requiresReauthorization = false) => {
    const { id, ...userData } = form;
    try {
      await updateUser({ id, userData }).unwrap();
      if (requiresReauthorization) {
        onEmailChange();
        return;
      }

      dispatch(fetchUser());
      setFormValidationErrors(null);
      setServerError('');
    } catch (error) {
      const key = error?.data?.message;
      const known = key && t(`errors.${key}`, { defaultValue: '' });
      setServerError(known || key || t('errors.generic'));
      console.error('Error updating user:', error);
    }
  };

  const handleSubmit = () => {
    const noChanges = !isDirty;
    if (noChanges) {
      return;
    }

    const { id, ...userData } = form;
    const validationErrors = validateFormData(t, putUserSchema, userData);
    if (!!validationErrors) {
      setFormValidationErrors(validationErrors);
      return;
    }

    if (form.email === user.email) {
      handleUpdateUser();
    } else {
      onEmailChange();
    }
  };

  const handleCancel = () => {
    setForm(user);
    setFormValidationErrors(null);
  };

  return (
    <SettingsCard>
      <SettingsCardHeader
        title={t('generic.account')}
        subtitle={t('userProfile.accountSubtitle')}
        tooltipText={t('userProfile.information')}
        alwaysEditable={true}
        isDirty={isDirty}
        onSave={handleSubmit}
        onCancel={handleCancel}
      />
      
      <SettingsCardContent>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
            gap: '20px',
          }}
        >
          <TextField
            label={t('generic.firstName')}
            variant="outlined"
            name="firstname"
            value={form.firstname}
            onChange={handleChange}
            fullWidth
            sx={commonTextFieldStylesWithDisabled}
            error={!!firstNameError}
            helperText={firstNameError ?? ' '}
          />
          <TextField
            label={t('generic.lastName')}
            variant="outlined"
            name="lastname"
            value={form.lastname}
            onChange={handleChange}
            fullWidth
            sx={commonTextFieldStylesWithDisabled}
            error={!!lastNameError}
            helperText={lastNameError ?? ' '}
          />
          <TextField
            label={t('generic.email')}
            variant="outlined"
            name="email"
            value={form.email}
            onChange={handleChange}
            fullWidth
            sx={{
              ...commonTextFieldStylesWithDisabled,
              gridColumn: { xs: '1', md: '1 / -1' },
            }}
            error={!!emailError}
            helperText={emailError ?? ' '}
          />
          <FormControl fullWidth>
            <TextField
              value={form.language}
              select
              label={t('userProfile.languagePreferences')}
              name="language"
              onChange={handleChange}
              fullWidth
              sx={commonTextFieldStylesWithDisabled}
              error={!!languageError}
            >
              {languages.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>
            <FormHelperText error>
              {languageError ?? ' '}
            </FormHelperText>
          </FormControl>
          {user?.role === 'admin' && (
            <FormControl fullWidth>
              <TextField
                value={form.role}
                select
                label={t('userProfile.permission')}
                name="role"
                onChange={handleChange}
                fullWidth
                sx={commonTextFieldStylesWithDisabled}
                error={!!roleError}
                disabled={isLastAdmin}
              >
                {permissions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </TextField>
              <FormHelperText error={!!roleError}>
                {roleError || (isLastAdmin ? t('errors.cannotRemoveLastAdmin') : ' ')}
              </FormHelperText>
            </FormControl>
          )}
        </Box>
        {serverError && (
          <Typography color="error" sx={{ mt: 2 }}>
            {serverError}
          </Typography>
        )}
      </SettingsCardContent>
      <ConfirmDiscardDialog {...confirmProps} />
    </SettingsCard>
  );
}

export default AccountTab;
